import type { createClient } from "@/lib/supabase/client";
import { AuthError } from "@supabase/supabase-js";

/**
 * Type representing a permission check result from the database
 */
type PermissionResult = {
  has_permission: boolean;
};

/**
 * Check if user has admin permissions based on a set of admin-level permissions
 */
export async function checkAdminPermissions(
  supabase: ReturnType<typeof createClient>,
  userId: string
): Promise<boolean> {
  try {
    const { data: hasAdminPermissions, error: permissionError } = await supabase.rpc(
      "check_multiple_permissions",
      {
        p_user_id: userId,
        p_permissions: [
          "roles.create",
          "roles.edit",
          "users.assign_roles",
          "games.create",
          "assets.create",
        ],
      }
    );

    if (permissionError) {
      console.error("Permission check error:", permissionError);
      return false;
    }

    return hasAdminPermissions?.some((p: PermissionResult) => p.has_permission) || false;
  } catch (error) {
    console.error("Error checking admin permissions:", error);
    return false;
  }
}

/**
 * Check if error is an expired session error
 */
export function isExpiredSessionError(error: unknown): boolean {
  return (
    error instanceof AuthError &&
    (error.code === "refresh_token_not_found" ||
      error.code === "token_refreshed" ||
      error.code === "session_not_found")
  );
}

/**
 * Safely validate and return a redirect path, ensuring it's an internal path
 * and not an external URL. Returns "/" for invalid or unsafe paths.
 *
 * This function protects against open redirect vulnerabilities by:
 * - Decoding URL-encoded sequences to detect bypass attempts
 * - Using URL parsing to detect absolute URLs or protocols
 * - Validating that paths are relative and start with "/"
 *
 * @param path - The redirect path to validate
 * @returns A safe internal path or "/" as fallback
 */
export function getSafeRedirectPath(path: string | undefined): string {
  if (!path) return "/";

  // Decode URL-encoded sequences first to detect bypass attempts
  let decodedPath: string;
  try {
    decodedPath = decodeURIComponent(path);
  } catch {
    return "/"; // Invalid encoding
  }

  // Must start with / and be a relative path
  if (!decodedPath.startsWith("/")) return "/";

  // Use URL parsing to detect absolute URLs or protocols
  try {
    const url = new URL(decodedPath, "http://dummy");
    // If URL parsing succeeds and has a protocol/host, it's absolute - reject
    if (url.protocol !== "http:" || url.hostname !== "dummy") {
      return "/";
    }
  } catch {
    // URL parsing failed - likely a relative path, continue validation
  }

  // Final checks: no protocol indicators, no double slashes
  if (decodedPath.includes("://") || decodedPath.includes("//")) {
    return "/";
  }

  return decodedPath;
}
